/**
 * Client-side AI stream telemetry (Option C part A).
 * In-memory + sessionStorage; AI Debugger reads by chatId.
 * Persist-to-API is a follow-up.
 */

export type StreamTelemetryStep = {
  at: number;
  type: string;
  label?: string;
  /** ms since stream start */
  elapsedMs: number;
  /** ms since previous recorded step (null for first) */
  deltaMs: number | null;
  /** ms until the next recorded phase/event */
  durationMs?: number;
};

export type StreamTelemetry = {
  chatId: string | null;
  agentName?: string | null;
  startedAt: number;
  endedAt?: number;
  endReason?: string;
  firstChunkAt?: number;
  heartbeatCount: number;
  eventCounts: Record<string, number>;
  lastEventType?: string;
  lastEventAt?: number;
  tools: string[];
  /** Ordered wire steps for AI Debugger (capped). */
  steps: StreamTelemetryStep[];
  /** Interleaved thinking on/off when BE sends it (Q&A connection_established). */
  thinking?: boolean;
  error?: string;
  durationMs?: number;
};

const STORAGE_KEY = "tf-ai-stream-telemetry";
const MAX_ENTRIES = 30;
/** Keep debugger useful without blowing sessionStorage. */
const MAX_STEPS = 80;

/** Event types that get a row in the step timeline (not every chunk/heartbeat). */
const STEP_EVENT_TYPES = new Set([
  "connection_established",
  "current_tool_use",
  "pipeline_step",
  "pipeline_complete",
  "pipeline_completed",
  "pipeline_cancelled",
  "dashboard_persisted",
  "message",
  "stream_complete",
  "new_dashboard",
  "template_chunk",
  "event",
  "error",
  "error-frame",
  "fatal",
]);

/** Rapid identical frames (template/generic) — keep one row per burst. */
const DEDUPE_STEP_TYPES = new Set([
  "current_tool_use",
  "template_chunk",
  "event",
]);

const memory = new Map<string, StreamTelemetry>();

function persist() {
  try {
    const entries = [...memory.entries()].slice(-MAX_ENTRIES);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // private mode / quota — memory still works for the tab
  }
}

function hydrate() {
  if (memory.size > 0) {
    return;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }
    const entries = JSON.parse(raw) as [string, StreamTelemetry][];
    for (const [key, value] of entries) {
      if (!value.steps) {
        value.steps = [];
      }
      memory.set(key, value);
    }
  } catch {
    // ignore corrupt storage
  }
}

function keyFor(chatId: string | null | undefined, fallback: string) {
  return chatId || fallback;
}

function pushStep(record: StreamTelemetry, type: string, label?: string): void {
  const at = Date.now();
  const prev = record.steps[record.steps.length - 1];
  if (prev && prev.durationMs == null) {
    prev.durationMs = at - prev.at;
  }
  record.steps.push({
    at,
    type,
    ...(label ? { label } : {}),
    elapsedMs: at - record.startedAt,
    deltaMs: prev ? at - prev.at : null,
  });
  if (record.steps.length > MAX_STEPS) {
    record.steps.splice(0, record.steps.length - MAX_STEPS);
  }
}

export function startStreamTelemetry(opts: {
  chatId?: string | null;
  agentName?: string | null;
  runKey: string;
}): StreamTelemetry {
  hydrate();
  const record: StreamTelemetry = {
    chatId: opts.chatId ?? null,
    agentName: opts.agentName ?? null,
    startedAt: Date.now(),
    heartbeatCount: 0,
    eventCounts: {},
    tools: [],
    steps: [],
  };
  memory.set(keyFor(opts.chatId, opts.runKey), record);
  persist();
  return record;
}

export function noteStreamEvent(
  runKey: string,
  chatId: string | null | undefined,
  type: string,
  extra?: {
    toolName?: string;
    adoptChatId?: string;
    thinking?: boolean;
    stepLabel?: string;
  }
): void {
  hydrate();
  const key = keyFor(chatId, runKey);
  let record = memory.get(key);
  if (!record && chatId) {
    record = memory.get(runKey);
    if (record) {
      memory.delete(runKey);
      record.chatId = chatId;
      memory.set(chatId, record);
    }
  }
  if (!record) {
    return;
  }

  if (extra?.adoptChatId && extra.adoptChatId !== record.chatId) {
    memory.delete(key);
    record.chatId = extra.adoptChatId;
    memory.set(extra.adoptChatId, record);
  }

  const eventType = type || "unknown";
  record.eventCounts[eventType] = (record.eventCounts[eventType] || 0) + 1;
  record.lastEventType = eventType;
  record.lastEventAt = Date.now();

  if (eventType === "heartbeat") {
    record.heartbeatCount += 1;
  }
  let didMutateStep = false;
  if (eventType === "message_chunk" && !record.firstChunkAt) {
    record.firstChunkAt = Date.now();
    pushStep(record, "first_chunk", "TTFB");
    didMutateStep = true;
  }
  if (extra?.toolName && !record.tools.includes(extra.toolName)) {
    record.tools.push(extra.toolName);
  }
  if (typeof extra?.thinking === "boolean") {
    record.thinking = extra.thinking;
  }

  if (STEP_EVENT_TYPES.has(eventType)) {
    const label =
      extra?.stepLabel ||
      (eventType === "current_tool_use" ? extra?.toolName : undefined);
    // Dedupe rapid identical progress pings (same type+label, <250ms apart).
    const prev = record.steps[record.steps.length - 1];
    const sameBurst =
      DEDUPE_STEP_TYPES.has(eventType) &&
      prev?.type === eventType &&
      prev.label === label &&
      Date.now() - prev.at < 250;
    if (!sameBurst) {
      pushStep(record, eventType, label);
      didMutateStep = true;
    }
  }
  // Skip sessionStorage on hot chunk/heartbeat path; memory map is live for
  // Debugger. Persist phase steps + start/end so navigation still recovers.
  if (
    didMutateStep ||
    (eventType !== "message_chunk" && eventType !== "heartbeat")
  ) {
    persist();
  }
}

export function endStreamTelemetry(
  runKey: string,
  chatId: string | null | undefined,
  endReason: string,
  error?: string
): void {
  hydrate();
  const record = memory.get(keyFor(chatId, runKey)) || memory.get(runKey);
  if (!record) {
    return;
  }
  record.endedAt = Date.now();
  record.endReason = endReason;
  record.durationMs = record.endedAt - record.startedAt;
  if (error) {
    record.error = error.slice(0, 800);
  }
  const last = record.steps[record.steps.length - 1];
  if (!last || last.type !== endReason) {
    pushStep(record, "end", endReason);
  }
  persist();
}

export function getStreamTelemetry(
  chatId: string | null | undefined
): StreamTelemetry | null {
  if (!chatId) {
    return null;
  }
  hydrate();
  return memory.get(chatId) ?? null;
}

export type StreamError = Error & {
  status?: number;
  retryAfter?: number | null;
  type?: string;
  cause?: unknown;
  original?: unknown;
};

function failureSummary(err: unknown): string {
  if (typeof err === "string") {
    return err;
  }
  if (err && typeof err === "object") {
    const o = err as {
      message?: unknown;
      error?: unknown;
      status?: unknown;
      name?: unknown;
    };
    if (typeof o.message === "string" && o.message.trim()) {
      return o.message;
    }
    if (typeof o.error === "string" && o.error.trim()) {
      return o.error;
    }
    if (typeof o.status === "number") {
      return `Request failed with status ${o.status}`;
    }
    if (typeof o.name === "string" && o.name !== "Error") {
      return o.name;
    }
  }
  return String(err ?? "Unknown stream error");
}

function isStreamError(err: unknown): err is StreamError {
  return (
    err instanceof Error && err.name === "StreamError" && "original" in err
  );
}

/**
 * Build a user/support-facing Error with enough context to troubleshoot
 * network drops / early closes (chat id, last SSE event, duration, etc.).
 */
export function describeStreamFailure(
  err: unknown,
  context: {
    chatId?: string | null;
    runKey?: string | null;
    endReason?: string | null;
  } = {}
): StreamError {
  // Already enriched by streamAIResponse — don't wrap twice.
  if (isStreamError(err)) {
    return err;
  }

  const summary = failureSummary(err);
  const record =
    (context.chatId &&
      (memory.get(context.chatId) ||
        (context.runKey
          ? memory.get(keyFor(context.chatId, context.runKey))
          : undefined))) ||
    (context.runKey ? memory.get(context.runKey) : undefined);

  const details: string[] = [];
  const errObj =
    err && typeof err === "object"
      ? (err as {
          name?: string;
          status?: number;
          type?: string;
          retryAfter?: unknown;
          cause?: { message?: string } | string;
        })
      : null;
  if (errObj?.name && errObj.name !== "Error") {
    details.push(`type=${errObj.name}`);
  }
  if (typeof errObj?.status === "number") {
    details.push(`status=${errObj.status}`);
  }
  if (errObj?.type) {
    details.push(`frame=${errObj.type}`);
  }
  const chatId = context.chatId ?? record?.chatId;
  if (chatId) {
    details.push(`chatId=${chatId}`);
  }
  if (context.endReason) {
    details.push(`end=${context.endReason}`);
  } else if (record?.endReason) {
    details.push(`end=${record.endReason}`);
  }
  const lastEvent = record?.lastEventType;
  if (lastEvent) {
    details.push(`lastEvent=${lastEvent}`);
  }
  const durationMs =
    record?.durationMs ??
    (record?.startedAt != null ? Date.now() - record.startedAt : null);
  if (durationMs != null) {
    details.push(`duration=${durationMs}ms`);
  }
  if (record?.heartbeatCount) {
    details.push(`heartbeats=${record.heartbeatCount}`);
  }
  const causeMsg =
    typeof errObj?.cause === "string" ? errObj.cause : errObj?.cause?.message;
  if (causeMsg) {
    details.push(`cause=${causeMsg}`);
  }

  const message =
    details.length > 0 ? `${summary} (${details.join(", ")})` : summary;
  const out = new Error(message) as StreamError;
  // Stable name so callers can detect already-enriched failures.
  out.name = "StreamError";
  if (typeof errObj?.status === "number") {
    out.status = errObj.status;
  }
  const retryAfter = errObj?.retryAfter;
  if (typeof retryAfter === "number" || retryAfter === null) {
    out.retryAfter = retryAfter;
  }
  if (errObj?.type) {
    out.type = errObj.type;
  }
  out.cause = err;
  out.original = err;
  return out;
}
